import React, { useEffect, useRef, useState } from "react";
import { Button, Checkbox, FormControlLabel, TableBody, TableCell, TableHead, TableRow, Typography } from "@material-ui/core";
import { Resource } from "react-admin";
import AddIcon from "@material-ui/icons/Add";
import { useTokenOps } from "../components/TokenContext";
import { carbonRequest } from "./corporateCarbon/api";
import {
  CarbonAdminPage,
  CarbonPageAction,
  CarbonConfirmDialog,
  CarbonDataTable,
  CarbonEditSurface,
  CarbonFilterBar,
  CarbonForm,
  CarbonFormToolbar,
  CarbonSection,
  CarbonSelectInput,
  CarbonStatus,
  CarbonTableActions,
  CarbonTableRow,
  CarbonTextInput,
} from "./corporateCarbon/CarbonAdminPage";
import { carbonContextFromSearch, carbonResourcePath, friendlyCarbonError, reportingRevisionStatusLabel as reportingRevisionLabel } from "./corporateCarbon/workflow";

const families = [
  "evidenceSource",
  "contractualInstrument",
  "energyType",
  "heatSteamSupplyScenario",
  "heatSteamFactorSource",
  "quantityUnit",
  "fleetBiofuelBlendFlag",
  "fuel",
];

const stationaryUnits = {
  natural_gas_cubic_metre: ["cubic_metre"],
  natural_gas_kilowatt_hour_pcs: ["kwh"],
  heating_gasoil_litre: ["litres"],
  generator_gasoil_litre: ["litres"],
  lpg_propane_litre: ["litres"],
  lpg_propane_kilogram: ["kilograms"],
  butane_kilogram: ["kilograms"],
  non_biogenic_pellet_biomass_kilogram: ["kilograms"],
  biogenic_pellet_biomass_kilogram: ["kilograms"],
  non_biogenic_woodchip_biomass_kilogram: ["kilograms"],
  biogenic_woodchip_biomass_kilogram: ["kilograms"],
  fuel_oil_litre: ["litres"],
  petrol: ["litres"],
  diesel: ["litres"],
  compressed_natural_gas: ["kilograms"],
  liquefied_natural_gas: ["kilograms"],
  lpg_autogas: ["litres"],
  other_specified_fuel: ["litres", "kilograms", "cubic_metre", "kwh"],
};

const stationaryBiomassByFuel = {
  natural_gas_cubic_metre: false,
  natural_gas_kilowatt_hour_pcs: false,
  heating_gasoil_litre: false,
  generator_gasoil_litre: false,
  lpg_propane_litre: false,
  lpg_propane_kilogram: false,
  butane_kilogram: false,
  non_biogenic_pellet_biomass_kilogram: false,
  biogenic_pellet_biomass_kilogram: true,
  non_biogenic_woodchip_biomass_kilogram: false,
  biogenic_woodchip_biomass_kilogram: true,
  fuel_oil_litre: false,
  petrol: false,
  diesel: false,
  compressed_natural_gas: false,
  liquefied_natural_gas: false,
  lpg_autogas: false,
  other_specified_fuel: false,
};

const Choice = ({ label, value, onChange, rows = [], disabled = false }) => (
  <CarbonSelectInput
    choices={rows.filter((row) => row.id === value || (!row.retired && row.status !== "retired" && row.siteStatus !== "archived"))}
    choiceText={(row) => row.label?.en || row.label || row.code}
    disabled={disabled}
    label={label}
    name={String(label).toLowerCase().replace(/[^a-z0-9]+/g, "-")}
    value={value || ""}
    onChange={(event) => onChange(event.target.value)}
  />
);

const NumberField = ({ label, value, onChange }) => (
  <CarbonTextInput label={label} name={String(label).toLowerCase().replace(/[^a-z0-9]+/g, "-")} type="number" inputProps={{ min: 0, step: "any" }} value={value ?? ""} onChange={(event) => onChange(event.target.value)} />
);

const formatActivityValue = (activity) => {
  const candidates = [
    [activity.payload?.consumptionKwh, "kWh"],
    [activity.payload?.quantity, ""],
    [activity.payload?.litersConsumed, "L"],
    [activity.payload?.kmDriven, "km"],
    [activity.payload?.kgRecharged, "kg"],
  ];
  const [raw, unit] = candidates.find(([value]) => value !== undefined && value !== null) || [];
  if (raw === undefined) return "—";
  const value = Number(raw);
  const formatted = Number.isFinite(value)
    ? new Intl.NumberFormat(undefined, { maximumFractionDigits: 6 }).format(value)
    : String(raw);
  return `${formatted}${unit ? ` ${unit}` : ""}`;
};

export const CarbonActivityPage = ({
  accessToken,
  history,
  initialRevision = null,
  initialSources = [],
  initialReferences = null,
  initialActivities = [],
  createOnly = false,
  editActivityId = "",
  listOnly = false,
  showActivityId = "",
}) => {
  const contextToken = useTokenOps().account?.accessToken;
  const token = accessToken || contextToken;
  const generation = useRef(0);
  const initialContext = useRef(
    carbonContextFromSearch(window.location.hash || window.location.search),
  );
  const pendingRef = useRef(false);
  const archivePendingRef = useRef(new Set());
  const tokenResetInitializedRef = useRef(false);
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const [revision, setRevision] = useState(initialRevision);
  const [inventories, setInventories] = useState([]);
  const [inventoryId, setInventoryId] = useState("");
  const [revisions, setRevisions] = useState([]);
  const [sources, setSources] = useState(initialSources);
  const [refs, setRefs] = useState(initialReferences || {});
  const [activities, setActivities] = useState(initialActivities);
  const [sourceId, setSourceId] = useState("");
  const [periodStart, setPeriodStart] = useState(
    initialRevision?.reportingStartDate || "",
  );
  const [periodEnd, setPeriodEnd] = useState(
    initialRevision?.reportingEndDate || "",
  );
  const [payload, setPayload] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [viewingId, setViewingId] = useState(showActivityId || null);
  const [useSubPeriod, setUseSubPeriod] = useState(false);
  const [pending, setPending] = useState(false);
  const [archivePending, setArchivePending] = useState([]);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [loading, setLoading] = useState(!initialRevision);
  const [error, setError] = useState("");
  const source = sources.find((row) => row.id === sourceId);
  const stationaryFuel = (refs.fuel || []).find(
    (row) => row.id === source?.configuration?.fuelId,
  );
  const stationaryBiomass =
    stationaryBiomassByFuel[stationaryFuel?.code] === true;
  const category = source?.category;
  const draft = revision?.status === "draft";

  useEffect(() => {
    if (!tokenResetInitializedRef.current) {
      tokenResetInitializedRef.current = true;
      return;
    }
    generation.current += 1;
    pendingRef.current = false;
    archivePendingRef.current.clear();
    setPending(false);
    setArchivePending([]);
    setArchiveTarget(null);
    setError("");
  }, [token]);

  useEffect(() => {
    if (initialRevision) return undefined;
    const currentGeneration = ++generation.current;
    setLoading(true);
    setError("");
    carbonRequest(token, "corporate-carbon/inventories")
      .then(async (inventories) => {
        const sites = await carbonRequest(
          token,
          "corporate-carbon/sites?includeArchived=true",
        );
        const sourceLists = await Promise.all(
          sites.map(async (site) => {
            const rows = await carbonRequest(
              token,
              `corporate-carbon/sites/${site.id}/sources?includeRetired=true`,
            );
            return rows.map((row) => ({ ...row, siteStatus: site.status }));
          }),
        );
        const referenceLists = await Promise.all(
          families.map((family) =>
            carbonRequest(
              token,
              `corporate-carbon/reference-data/${family}?includeRetired=true&perPage=1000`,
            ),
          ),
        );
        if (currentGeneration === generation.current) {
          setInventories(inventories);
          setSources(sourceLists.flat());
          setRefs(
            Object.fromEntries(
              families.map((family, index) => [
                family,
                referenceLists[index].data,
              ]),
            ),
          );
          const requestedInventory =
            inventories.find(({ id }) => id === initialContext.current.inventoryId) ||
            inventories.find(({ status }) => status === "active") ||
            inventories[0];
          if (requestedInventory) {
            const versionRows = await carbonRequest(token, `corporate-carbon/inventories/${requestedInventory.id}/revisions`);
            if (currentGeneration !== generation.current) return;
            setInventoryId(requestedInventory.id);
            setRevisions(versionRows);
            const requestedRevision =
              versionRows.find(({ id }) => id === initialContext.current.revisionId) ||
              versionRows[0];
            if (requestedRevision) {
              const activityRows = await carbonRequest(token, `corporate-carbon/revisions/${requestedRevision.id}/activities?includeArchived=true`);
              if (currentGeneration !== generation.current) return;
              setRevision(requestedRevision);
              setPeriodStart(requestedRevision.reportingStartDate);
              setPeriodEnd(requestedRevision.reportingEndDate);
              setActivities(activityRows);
            }
          }
        }
      })
      .catch(
        (requestError) =>
          currentGeneration === generation.current &&
          setError(friendlyCarbonError(requestError)),
      )
      .finally(
        () => currentGeneration === generation.current && setLoading(false),
      );
    return () => {
      if (currentGeneration === generation.current) generation.current += 1;
    };
  }, [token, initialRevision]);

  const selectInventory = async (id) => {
    const currentGeneration = ++generation.current;
    setInventoryId(id);
    setRevision(null);
    setRevisions([]);
    setActivities([]);
    setSourceId("");
    setPayload({});
    if (!id) return;
    try {
      const rows = await carbonRequest(
        token,
        `corporate-carbon/inventories/${id}/revisions`,
      );
      if (currentGeneration === generation.current) setRevisions(rows);
    } catch (requestError) {
      if (currentGeneration === generation.current)
        setError(friendlyCarbonError(requestError));
    }
  };
  const selectRevision = async (id) => {
    const currentGeneration = ++generation.current;
    const selected = revisions.find((row) => row.id === id);
    setRevision(selected || null);
    setActivities([]);
    setSourceId("");
    setPayload({});
    if (!selected) return;
    setPeriodStart(selected.reportingStartDate);
    setPeriodEnd(selected.reportingEndDate);
    try {
      const rows = await carbonRequest(
        token,
        `corporate-carbon/revisions/${id}/activities?includeArchived=true`,
      );
      if (currentGeneration === generation.current) setActivities(rows);
    } catch (requestError) {
      if (currentGeneration === generation.current)
        setError(friendlyCarbonError(requestError));
    }
  };

  const set = (key, value) =>
    setPayload((current) => ({ ...current, [key]: value }));
  const exactPayload = () => {
    const number = (value) => Number(value);
    if (category === "electricity")
      return {
        consumptionKwh: number(payload.consumptionKwh),
        evidenceSourceId: payload.evidenceSourceId,
        ...(payload.contractualInstrumentId
          ? { contractualInstrumentId: payload.contractualInstrumentId }
          : {}),
        ...(payload.supplierName ? { supplierName: payload.supplierName } : {}),
        ...(payload.marketBasedFactor !== undefined &&
        payload.marketBasedFactor !== null &&
        payload.marketBasedFactor !== ""
          ? { marketBasedFactor: number(payload.marketBasedFactor) }
          : {}),
        ...(payload.certificateReference
          ? { certificateReference: payload.certificateReference }
          : {}),
      };
    if (category === "purchased_energy")
      return {
        energyTypeId: payload.energyTypeId,
        consumptionKwh: number(payload.consumptionKwh),
        supplyScenarioId: payload.supplyScenarioId,
        customFactorValue: number(payload.customFactorValue),
        customFactorUnit: "kg_co2e_per_kwh",
        customFactorSourceId: payload.customFactorSourceId,
        evidenceSourceId: payload.evidenceSourceId,
        evidenceReference: payload.evidenceReference,
      };
    if (category === "stationary_combustion")
      return {
        quantity: number(payload.quantity),
        unitId: payload.unitId,
        evidenceSourceId: payload.evidenceSourceId,
        isBiomass: stationaryBiomass,
      };
    if (category === "mobile_combustion")
      return {
        inputMode: payload.inputMode,
        ...(payload.inputMode === "liters"
          ? { litersConsumed: number(payload.litersConsumed) }
          : {
              kmDriven: number(payload.kmDriven),
              averageConsumptionLitersPer100Km: number(
                payload.averageConsumptionLitersPer100Km,
              ),
            }),
        biofuelBlendFlagId: payload.biofuelBlendFlagId,
        evidenceSourceId: payload.evidenceSourceId,
      };
    return {
      kgRecharged: number(payload.kgRecharged),
      evidenceSourceId: payload.evidenceSourceId,
    };
  };
  const hasMissingRequired = (body) => {
    const required = [body.evidenceSourceId];
    if (category === "purchased_energy")
      required.push(
        body.energyTypeId,
        body.supplyScenarioId,
        body.customFactorSourceId,
        body.evidenceReference,
      );
    if (category === "stationary_combustion") required.push(body.unitId);
    if (category === "mobile_combustion")
      required.push(body.inputMode, body.biofuelBlendFlagId);
    return required.some(
      (value) => typeof value !== "string" || value.trim().length === 0,
    );
  };
  const submit = async (event) => {
    event.preventDefault();
    if (pendingRef.current) return;
    const activityPayload = exactPayload();
    if (hasMissingRequired(activityPayload)) {
      setError("Complete all required activity fields.");
      return;
    }
    if (
      periodStart < revision.reportingStartDate ||
      periodEnd > revision.reportingEndDate ||
      periodStart > periodEnd ||
      Object.entries(activityPayload).some(
        ([key, value]) =>
          typeof value === "number" &&
          (!Number.isFinite(value) ||
            (key === "marketBasedFactor" ? value < 0 : value <= 0)),
      )
    ) {
      setError("Enter a positive quantity and a period within the revision.");
      return;
    }
    const currentGeneration = generation.current;
    pendingRef.current = true;
    setPending(true);
    setError("");
    const identity = editingId
      ? {}
      : {
          revisionId: revision.id,
          sourceId: source.id,
          siteId: source.siteId,
          entrySource: "manual",
        };
    try {
      await carbonRequest(
        token,
        editingId
          ? `corporate-carbon/activities/${editingId}`
          : "corporate-carbon/activities",
        {
          method: editingId ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...identity,
            category,
            periodStart,
            periodEnd,
            payload: activityPayload,
          }),
        },
      );
      if (currentGeneration === generation.current) {
        const rows = await carbonRequest(
          token,
          `corporate-carbon/revisions/${revision.id}/activities?includeArchived=true`,
        );
        if (currentGeneration === generation.current) {
          setActivities(rows);
          if (editActivityId && history) {
            history.push(carbonResourcePath("carbonActivities", editActivityId, "show", { inventoryId, revisionId: revision.id }));
          } else if (createOnly && history) {
            history.push(`/carbonActivities?inventoryId=${encodeURIComponent(inventoryId)}&revisionId=${encodeURIComponent(revision.id)}`);
          }
          setEditingId(null);
          setSourceId("");
          setPayload({});
        }
      }
    } catch (requestError) {
      if (currentGeneration === generation.current)
        setError(requestError.message);
    } finally {
      pendingRef.current = false;
      if (currentGeneration === generation.current) setPending(false);
    }
  };
  const edit = (activity) => {
    setEditingId(activity.id);
    setViewingId(activity.id);
    setSourceId(activity.sourceId);
    setPeriodStart(activity.periodStart);
    setPeriodEnd(activity.periodEnd);
    setUseSubPeriod(
      activity.periodStart !== revision.reportingStartDate ||
        activity.periodEnd !== revision.reportingEndDate,
    );
    setPayload(activity.payload || {});
    setError("");
  };
  useEffect(() => {
    if (!editActivityId || !revision || editingId === editActivityId) return;
    const activity = activities.find((item) => item.id === editActivityId);
    if (!activity) return;
    setEditingId(activity.id);
    setViewingId(activity.id);
    setSourceId(activity.sourceId);
    setPeriodStart(activity.periodStart);
    setPeriodEnd(activity.periodEnd);
    setUseSubPeriod(
      activity.periodStart !== revision.reportingStartDate ||
        activity.periodEnd !== revision.reportingEndDate,
    );
    setPayload(activity.payload || {});
    setError("");
  }, [activities, editActivityId, editingId, revision]);
  const archive = async (id) => {
    if (archivePendingRef.current.has(id)) return;
    const currentGeneration = generation.current;
    const currentToken = token;
    archivePendingRef.current.add(id);
    setArchivePending(Array.from(archivePendingRef.current));
    try {
      await carbonRequest(token, `corporate-carbon/activities/${id}/archive`, {
        method: "POST",
      });
      if (
        currentGeneration === generation.current &&
        currentToken === tokenRef.current
      )
        setActivities((rows) =>
          rows.map((row) =>
            row.id === id ? { ...row, status: "archived" } : row,
          ),
        );
        setArchiveTarget(null);
    } catch (requestError) {
      if (
        currentGeneration === generation.current &&
        currentToken === tokenRef.current
      )
        setError(requestError.message);
    } finally {
      archivePendingRef.current.delete(id);
      if (
        currentGeneration === generation.current &&
        currentToken === tokenRef.current
      )
        setArchivePending(Array.from(archivePendingRef.current));
    }
  };

  const common = (
    <>
      <FormControlLabel
        control={<Checkbox checked={useSubPeriod} onChange={(event) => {
          setUseSubPeriod(event.target.checked);
          if (!event.target.checked) {
            setPeriodStart(revision.reportingStartDate);
            setPeriodEnd(revision.reportingEndDate);
          }
        }} />}
        label="Use a sub-period"
      />
      {useSubPeriod && (
        <>
          <CarbonTextInput label="Period start" name="periodStart" type="date" value={periodStart} onChange={(event) => setPeriodStart(event.target.value)} InputLabelProps={{ shrink: true }} />
          <CarbonTextInput label="Period end" name="periodEnd" type="date" value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} InputLabelProps={{ shrink: true }} />
        </>
      )}
    </>
  );
  const evidence = (
    <Choice
      label="Evidence source"
      value={payload.evidenceSourceId}
      onChange={(value) => set("evidenceSourceId", value)}
      rows={(refs.evidenceSource || []).filter((row) => {
        const prefix =
          category === "electricity" || category === "purchased_energy"
            ? "electricity_heat_steam_"
            : category === "stationary_combustion"
              ? "stationary_combustion_"
              : category === "mobile_combustion"
                ? "fleet_"
                : "refrigerant_";
        return (
          row.id === payload.evidenceSourceId || row.code?.startsWith(prefix)
        );
      })}
    />
  );
  const stationaryUnitRows = (refs.quantityUnit || []).filter((unit) => {
    return stationaryUnits[stationaryFuel?.code]?.includes(unit.code);
  });
  const cancelEdit = () => {
    setEditingId(null);
    setSourceId("");
    setPeriodStart(revision?.reportingStartDate || "");
    setPeriodEnd(revision?.reportingEndDate || "");
    setUseSubPeriod(false);
    setPayload({});
    setError("");
  };
  const renderActivityForm = (form) =>
    editingId ? (
      <CarbonEditSurface
        dedicated={Boolean(editActivityId)}
        description="The emission source remains fixed because it defines the activity site, category, and reference configuration."
        onClose={() => {
          if (!pending) cancelEdit();
        }}
        open
        title={`Edit ${category?.replace(/_/g, " ") || "activity"} activity`}
      >
        {error ? <Typography role="alert">{error}</Typography> : null}
        {form}
      </CarbonEditSurface>
    ) : createOnly ? (
      <CarbonEditSurface
        dedicated
        description="Choose an emission source, enter its activity value, and attach the reporting evidence."
        open
        title="Create activity"
      >
        {form}
      </CarbonEditSurface>
    ) : form;
  const shownActivity = showActivityId
    ? activities.find((activity) => activity.id === showActivityId)
    : null;

  return (
    <CarbonAdminPage
      title={editActivityId ? "Edit activity" : createOnly ? "Create activity" : showActivityId ? "Activity details" : "Activity data"}
      description={editActivityId ? "Update activity data for the selected reporting revision." : "Enter source activity data and evidence for the selected inventory revision."}
      variant={listOnly ? "list" : "show"}
      actions={listOnly && revision?.status === "draft" ? (
        <CarbonPageAction
          color="primary"
          startIcon={<AddIcon />}
          to={carbonResourcePath("carbonActivities", null, "create", { inventoryId, revisionId: revision.id })}
        >
          Create activity
        </CarbonPageAction>
      ) : shownActivity && draft && shownActivity.status === "active" ? (
        <CarbonPageAction
          color="primary"
          to={carbonResourcePath("carbonActivities", shownActivity.id, "edit", { inventoryId, revisionId: revision?.id })}
        >
          Edit
        </CarbonPageAction>
      ) : null}
    >
        {!initialRevision && !showActivityId && !editActivityId && (
          <CarbonFilterBar label="Activity filters">
            <Choice
              label="Reporting year"
              value={inventoryId}
              onChange={selectInventory}
              rows={inventories.map((row) => ({
                ...row,
                label: String(row.reportingYear),
              }))}
            />
            <Choice
              label="Reporting data"
              value={revision?.id}
              onChange={selectRevision}
              rows={revisions.map((row) => ({
                ...row,
                label: reportingRevisionLabel(row),
              }))}
            />
          </CarbonFilterBar>
        )}
        {loading && <div role="status">Loading activities...</div>}
        {error && <Typography role="alert">{error}</Typography>}
        {!loading && showActivityId && !shownActivity ? <Typography role="alert">Activity not found.</Typography> : null}
        {showActivityId && shownActivity ? (
          <CarbonSection title={shownActivity.category.replace(/_/g, " ")} description="Read-only source activity data for the selected reporting data.">
            <dl>
              <dt>Emission source</dt><dd>{sources.find((item) => item.id === shownActivity.sourceId)?.label || shownActivity.sourceId}</dd>
              <dt>Site</dt><dd>{sources.find((item) => item.id === shownActivity.sourceId)?.siteId || shownActivity.siteId}</dd>
              <dt>Period</dt><dd>{shownActivity.periodStart} – {shownActivity.periodEnd}</dd>
              <dt>Activity value</dt><dd>{formatActivityValue(shownActivity)}</dd>
              <dt>Status</dt><dd><CarbonStatus value={shownActivity.status} /></dd>
              {Object.entries(shownActivity.payload || {}).map(([key, value]) => <React.Fragment key={key}><dt>{key.replace(/([A-Z])/g, " $1")}</dt><dd>{String(value ?? "—")}</dd></React.Fragment>)}
            </dl>
          </CarbonSection>
        ) : null}
        {!showActivityId && !editActivityId && !createOnly && <CarbonSection title="Activity history" description="Recorded consumption and operational activity for the selected reporting data.">
          {activities.length ? (
            <CarbonDataTable label="Carbon activities">
              <TableHead><TableRow><TableCell>Category</TableCell><TableCell>Emission source</TableCell><TableCell>Period</TableCell><TableCell>Activity value</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
              <TableBody>{activities.map((activity) => (
                <React.Fragment key={activity.id}>
                <CarbonTableRow
                  ariaLabel={`View ${activity.category.replace(/_/g, " ")} activity`}
                  onSelect={history ? () => history.push(carbonResourcePath("carbonActivities", activity.id, "show", { inventoryId, revisionId: revision?.id })) : undefined}
                >
                  <TableCell>{activity.category.replace(/_/g, " ")}</TableCell>
                  <TableCell>{sources.find((item) => item.id === activity.sourceId)?.label || activity.sourceId}</TableCell>
                  <TableCell>{activity.periodStart} – {activity.periodEnd}</TableCell>
                  <TableCell>{formatActivityValue(activity)}</TableCell>
                  <TableCell><CarbonStatus value={activity.status} /></TableCell>
                  <TableCell align="right">
                    <CarbonTableActions label={`Actions for ${activity.category}`}>
                      {draft && activity.status === "active" && <Button onClick={(event) => { event.stopPropagation(); history ? history.push(carbonResourcePath("carbonActivities", activity.id, "edit", { inventoryId, revisionId: revision?.id })) : edit(activity); }} size="small">Edit</Button>}
                      {draft && activity.status === "active" && <Button color="secondary" disabled={archivePending.includes(activity.id)} onClick={(event) => { event.stopPropagation(); setArchiveTarget(activity); }} size="small">Archive</Button>}
                    </CarbonTableActions>
                  </TableCell>
                </CarbonTableRow>
                {!listOnly && viewingId === activity.id && (
                  <TableRow><TableCell colSpan={6}>
                    <dl>
                      <dt>Emission source</dt><dd>{sources.find((item) => item.id === activity.sourceId)?.label || activity.sourceId}</dd>
                      <dt>Site</dt><dd>{sources.find((item) => item.id === activity.sourceId)?.siteId || activity.siteId}</dd>
                      <dt>Period</dt><dd>{activity.periodStart} – {activity.periodEnd}</dd>
                      {Object.entries(activity.payload || {}).map(([key, value]) => <React.Fragment key={key}><dt>{key.replace(/([A-Z])/g, " $1")}</dt><dd>{String(value ?? "—")}</dd></React.Fragment>)}
                    </dl>
                  </TableCell></TableRow>
                )}
                </React.Fragment>
              ))}</TableBody>
            </CarbonDataTable>
          ) : (
            <Typography>No activities</Typography>
          )}
        </CarbonSection>}
        {!listOnly && !showActivityId && draft && renderActivityForm(
          <CarbonForm aria-label="Activity form" onSubmit={submit}>
            <Choice
              label="Emission source"
              value={sourceId}
              disabled={Boolean(editingId)}
              onChange={(value) => {
                setSourceId(value);
                setPayload({});
              }}
              rows={sources}
            />
            {editingId && <Typography color="textSecondary">Emission source cannot be changed because it defines the activity site, category, and reference configuration. Archive this activity and create a new one to move it.</Typography>}
            {source && common}
            {category === "electricity" && (
              <>
                <NumberField
                  label="Consumption kWh"
                  value={payload.consumptionKwh}
                  onChange={(value) => set("consumptionKwh", value)}
                />
                {evidence}
                <Choice
                  label="Contractual instrument"
                  value={payload.contractualInstrumentId}
                  onChange={(value) => set("contractualInstrumentId", value)}
                  rows={(refs.contractualInstrument || []).filter((row) => row.code !== "Residual mix")}
                />
                {!payload.contractualInstrumentId && <Typography color="textSecondary">Without a contractual instrument, the calculation uses the country residual mix automatically when available.</Typography>}
                {payload.contractualInstrumentId && <CarbonTextInput label="Supplier name" name="supplierName" value={payload.supplierName || ""} onChange={(event) => set("supplierName", event.target.value)} />}
                {payload.contractualInstrumentId && <NumberField label="Market-based factor" value={payload.marketBasedFactor} onChange={(value) => set("marketBasedFactor", value)} />}
                {payload.contractualInstrumentId && <CarbonTextInput label="Certificate reference" name="certificateReference" value={payload.certificateReference || ""} onChange={(event) => set("certificateReference", event.target.value)} />}
              </>
            )}
            {category === "purchased_energy" && (
              <>
                <Choice
                  label="Energy type"
                  value={payload.energyTypeId}
                  onChange={(value) => set("energyTypeId", value)}
                  rows={refs.energyType}
                />
                <NumberField
                  label="Consumption kWh"
                  value={payload.consumptionKwh}
                  onChange={(value) => set("consumptionKwh", value)}
                />
                <Choice
                  label="Supply scenario"
                  value={payload.supplyScenarioId}
                  onChange={(value) => set("supplyScenarioId", value)}
                  rows={refs.heatSteamSupplyScenario}
                />
                <NumberField
                  label="Custom factor value"
                  value={payload.customFactorValue}
                  onChange={(value) => set("customFactorValue", value)}
                />
                <CarbonTextInput label="Custom factor unit" name="customFactorUnit" value="kg CO2e/kWh" InputProps={{ readOnly: true }} />
                <Choice
                  label="Custom factor source"
                  value={payload.customFactorSourceId}
                  onChange={(value) => set("customFactorSourceId", value)}
                  rows={refs.heatSteamFactorSource}
                />
                {evidence}
                <CarbonTextInput label="Evidence reference" name="evidenceReference" value={payload.evidenceReference || ""} onChange={(event) => set("evidenceReference", event.target.value)} />
              </>
            )}
            {category === "stationary_combustion" && (
              <>
                <NumberField
                  label="Quantity"
                  value={payload.quantity}
                  onChange={(value) => set("quantity", value)}
                />
                <Choice
                  label="Unit"
                  value={payload.unitId}
                  onChange={(value) => set("unitId", value)}
                  rows={stationaryUnitRows}
                />
                {evidence}
                <FormControlLabel control={<Checkbox checked={stationaryBiomass} disabled inputProps={{ "aria-label": "Biomass" }} />} label="Biomass" />
              </>
            )}
            {category === "mobile_combustion" && (
              <>
                <Choice
                  label="Input mode"
                  value={payload.inputMode}
                  onChange={(value) => setPayload({ inputMode: value })}
                  rows={[
                    { id: "liters", label: { en: "Liters" } },
                    { id: "km", label: { en: "Distance" } },
                  ]}
                />
                {payload.inputMode === "liters" && (
                  <NumberField
                    label="Liters consumed"
                    value={payload.litersConsumed}
                    onChange={(value) => set("litersConsumed", value)}
                  />
                )}
                {payload.inputMode === "km" && (
                  <>
                    <NumberField
                      label="Km driven"
                      value={payload.kmDriven}
                      onChange={(value) => set("kmDriven", value)}
                    />
                    <NumberField
                      label="Average consumption l/100km"
                      value={payload.averageConsumptionLitersPer100Km}
                      onChange={(value) =>
                        set("averageConsumptionLitersPer100Km", value)
                      }
                    />
                  </>
                )}
                <Choice
                  label="Biofuel blend"
                  value={payload.biofuelBlendFlagId}
                  onChange={(value) => set("biofuelBlendFlagId", value)}
                  rows={refs.fleetBiofuelBlendFlag}
                />
                {evidence}
              </>
            )}
            {category === "refrigerant" && (
              <>
                <NumberField
                  label="Kg recharged"
                  value={payload.kgRecharged}
                  onChange={(value) => set("kgRecharged", value)}
                />
                {evidence}
              </>
            )}
            {source && (
              <CarbonFormToolbar>
                <Button color="primary" type="submit" disabled={pending} variant="contained">Save activity</Button>
                {editingId ? <Button disabled={pending} onClick={cancelEdit}>Cancel</Button> : null}
              </CarbonFormToolbar>
            )}
          </CarbonForm>,
        )}
        <CarbonConfirmDialog
          confirmLabel={archiveTarget && archivePending.includes(archiveTarget.id) ? "Archiving…" : "Archive"}
          description="The activity will remain in reporting history but will no longer be active."
          disabled={Boolean(archiveTarget && archivePending.includes(archiveTarget.id))}
          onCancel={() => setArchiveTarget(null)}
          onConfirm={() => archiveTarget && archive(archiveTarget.id)}
          open={Boolean(archiveTarget)}
          title="Archive activity?"
        />
    </CarbonAdminPage>
  );
};

export const CarbonActivityList = (props) => <CarbonActivityPage {...props} listOnly />;
export const CarbonActivityShow = (props) => <CarbonActivityPage {...props} showActivityId={props.id} />;
export const CarbonActivityCreate = (props) => <CarbonActivityPage {...props} createOnly />;
export const CarbonActivityEdit = (props) => <CarbonActivityPage {...props} editActivityId={props.id} />;

const carbonActivityResource = () => (
  <Resource
    name="carbonActivities"
    options={{ label: "Activities", menuGroup: "Carbon Accounting" }}
    list={CarbonActivityList}
    show={CarbonActivityShow}
    create={CarbonActivityCreate}
    edit={CarbonActivityEdit}
  />
);
export default carbonActivityResource;
